import {useContext, useState, useEffect} from "react"
import styles from "./VoiceCallStudentPanel.module.css"
import {VoiceCallContext} from "context/VoiceCallContext"
import {UserAvatar} from "sections/NewCommunication/shared"
import cx from "classnames"
import {BASE_DEPARTMENT_NAME} from "types/departments"
import {isBrowserSoftphone} from "helpers/voiceDialer"
import moment from "moment"
import {ReactComponent as DialerIcon} from "./assets/dialer.svg"
import {Icon} from "components/Icon"
import {Tooltip} from "antd"
import {formatPhoneNumber, openLinkInNewTab} from "helpers"
import {ReactComponent as MuteIcon} from "./assets/mute.svg"
import {ReactComponent as UnmuteIcon} from "./assets/unmute.svg"
import {ReactComponent as ForwardIcon} from "./assets/forward.svg"
import {useVisible} from "hooks"
import {VoiceCallForwarding} from "../VoiceCallForwarding"
import {VoiceCallIVR} from "../VoiceCallIVR"
import {AutoDialer} from "types/auto-dialer"

export function VoiceCallStudentPanel() {
    const {
        calling,
        activeStudent,
        callStatus,
        isConnected,
        hangup,
        queuedStudents,
        isAuto,
        activeDepartmentId,
        voiceProvider,
        isIncomingCall,
        rejectCall,
        acceptCall,
        isInTransitionCall,
        transitionCountdown,
        connectedTime,
        forceCloseCall,
        isMuted,
        toggleMute
    } = useContext(VoiceCallContext)
    const [elapsed, setElapsed] = useState(0)
    const showCallForwarding = useVisible(false)
    const showCallIVR = useVisible(false)

    const showCancelWhileConnecting =
        isBrowserSoftphone(voiceProvider) &&
        calling &&
        !isConnected &&
        !isIncomingCall &&
        !isInTransitionCall &&
        callStatus !== AutoDialer.DialerStatus.Ended

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null
        if (isConnected && connectedTime) {
            const updateElapsed = () => {
                setElapsed(moment().diff(moment(connectedTime), "seconds"))
            }
            updateElapsed()
            interval = setInterval(updateElapsed, 1000)
        } else {
            setElapsed(0)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [isConnected, connectedTime])

    if ((queuedStudents.length === 0 && !calling) || !activeStudent) return null

    const nextStudent = queuedStudents?.[0]

    return (
        <>
            <div className={styles.container}>
                <Tooltip title="Close">
                    <div className={styles.closeBtn} onClick={forceCloseCall}>
                        <Icon icon="CLOSE" color="#ffffff" className={styles.closeIcon} />
                    </div>
                </Tooltip>
                <div className={styles.callContainer}>
                    <UserAvatar user={activeStudent as any} className={styles.avatar} /> : <div />
                    {isAuto ? <div className={styles.autoContainer}>Auto</div> : <div />}
                </div>
                {isInTransitionCall && transitionCountdown > 0 ? (
                    <span className={styles.status}>next call in {transitionCountdown}s</span>
                ) : (
                    <div className={styles.info}>
                        {activeDepartmentId && (
                            <div className={styles.departmentContainer}>{BASE_DEPARTMENT_NAME[activeDepartmentId]}</div>
                        )}
                        {activeStudent.campusName && (
                            <div className={styles.campusContainer}>
                                <Icon icon="LOCATION" color="#a0a0a0" className={styles.campusIcon} />
                                <span>{activeStudent.campusName}</span>
                            </div>
                        )}
                        {activeStudent.userId && (
                            <div
                                className={styles.nameWrap}
                                onClick={() => {
                                    openLinkInNewTab(`/users/${activeStudent.userId}`)
                                }}>
                                <span className={styles.name}>{activeStudent.fullName}</span>
                                <Icon icon="EYE" color="#fff" className={styles.linkIcon} />
                            </div>
                        )}
                        <span className={styles.phone}>{activeStudent.phone}</span>
                        {isConnected && connectedTime && (
                            <span className={styles.connectedTime}>{moment.utc(elapsed * 1000).format("mm:ss")}</span>
                        )}
                        {isIncomingCall && !isConnected ? (
                            <>
                                <span className={styles.status}>calling you</span>
                                <div className={styles.incomingBtnWrap}>
                                    <div className={styles.rejectBtn} onClick={() => rejectCall()}>
                                        Reject
                                    </div>
                                    <div className={styles.acceptBtn} onClick={() => acceptCall()}>
                                        Accept
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className={styles.status}>{callStatus}...</span>
                                {showCancelWhileConnecting && (
                                    <Tooltip title="Cancel call">
                                        <div
                                            className={cx(styles.btnWrap, styles.btnHangup, styles.btnCancelConnecting)}
                                            onClick={hangup}>
                                            <DialerIcon className={styles.icon} />
                                            <span className={styles.title}>Cancel</span>
                                        </div>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                )}
                <div className={cx(styles.ivrContainer, {[styles.ivrContainerVisible]: showCallIVR.isVisible})}>
                    <VoiceCallIVR isVisible={showCallIVR.isVisible} />
                </div>
                {isBrowserSoftphone(voiceProvider) && !isInTransitionCall && isConnected && (
                    <>
                        <div className={styles.actionBtnWrap}>
                            <div className={cx(styles.actionBtn, styles.btnMute)} onClick={() => toggleMute()}>
                                {isMuted ? (
                                    <MuteIcon className={styles.icon} />
                                ) : (
                                    <UnmuteIcon className={styles.icon} />
                                )}
                            </div>
                            <div className={cx(styles.actionBtn, styles.btnForward)} onClick={showCallForwarding.open}>
                                <ForwardIcon className={styles.icon} />
                            </div>
                            <div className={cx(styles.actionBtn, styles.btnForward)} onClick={showCallIVR.toggle}>
                                <Icon icon="DIALPAD" className={styles.icon} color="#fff" />
                            </div>
                        </div>
                        <div className={cx(styles.btnWrap, styles.btnHangup)} onClick={hangup}>
                            <DialerIcon className={styles.icon} />
                            <span className={styles.title}>Hang Up</span>
                        </div>
                    </>
                )}
                {nextStudent && (
                    <div className={styles.nextContainer}>
                        <span className={styles.nextTitle}>Next: </span>
                        <span className={styles.nextStudent}>{nextStudent.fullName}</span>
                        <span className={styles.nextPhone}>{formatPhoneNumber(nextStudent.phone)}</span>
                    </div>
                )}
            </div>
            <VoiceCallForwarding
                open={showCallForwarding.isVisible}
                onClose={showCallForwarding.close}
                departmentId={activeDepartmentId}
            />
        </>
    )
}
